import { HttpContext } from "@angular/common/http";
import { Component, OnInit, inject } from "@angular/core";
import {
  ConfirmationService,
  ToastMessageOptions,
  MessageService
} from "primeng/api";
import {
  EntityModelJobDto,
  EntityModelJobExecutionDto,
  ImportAdminControllerService,
  JobsService,
  JobInstanceDto,
  PagedModelEntityModelLogMessageDto,
  EntityModelLogMessageDto,
  RollbackReport,
  RollbackStatusDto,
  EntityModelJobConfigDto,
  EntityModelJobExecutionDto as JobExecutionEntityModel,
  JobFileInputDto,
  CollectionModelString
} from "../../../../modules/lipidcompass-data-importer-client";
import { ColumnFilterType, TableColumn } from "../../_models/table-column";
import { LoggingService } from "../../_services/logging.service";
import { FormsModule } from "@angular/forms";
import { TableModule } from "primeng/table";
import { CommonModule, DatePipe, JsonPipe } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { PaginatorModule } from "primeng/paginator";
import { SubmissionControllerService } from "../../../../modules/lipidcompass-backend-client";
import { RouterModule } from "@angular/router";
import { AccordionModule } from "primeng/accordion";
import { ToastModule } from "primeng/toast";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { InputNumberModule } from "primeng/inputnumber";
import { SelectModule } from "primeng/select";
import { CheckboxModule } from "primeng/checkbox";
import { CardModule } from "primeng/card";
import { FieldsetModule } from "primeng/fieldset";
import { TooltipModule } from "primeng/tooltip";

const MZTAB_JOB_NAME = "mzTabResultImportJob";

// The job parameter whose presence in a job config marks the job as file-based.
const FILE_NAME_PARAMETER = "fileName";

@Component({
  selector: "app-admin-dataimport-jobs",
  templateUrl: "./admin-dataimport-jobs.component.html",
  styleUrls: ["./admin-dataimport-jobs.component.css"],
  standalone: true,
  imports: [
    AccordionModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    CommonModule,
    ConfirmDialogModule,
    DialogModule,
    FieldsetModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    JsonPipe,
    PaginatorModule,
    RouterModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule
  ]
})
export class AdminDataimportJobsComponent implements OnInit {
  private controller = inject(JobsService);
  private importAdminController = inject(ImportAdminControllerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private loggingService = inject(LoggingService);
  protected submissionController = inject(SubmissionControllerService);

  columns!: TableColumn[];
  filterFields!: string[];
  jobDtos: EntityModelJobDto[] | undefined = [];
  selectedJobDto: EntityModelJobDto | null = null;
  jobConfigDtos: EntityModelJobConfigDto[] | undefined = [];
  selectedJobConfigDto: EntityModelJobConfigDto | null = null;
  jobInstanceDtos: any[] = [];
  selectedJobInstanceDto: any = null;
  jobExecutions: EntityModelJobExecutionDto[] = [];
  expandedRows = {};

  // Job launch dialog state
  jobLaunchDialogVisible = false;
  availableFiles: string[] = [];
  selectedFile: string | null = null;
  customParameters: { [key: string]: string | number | boolean } = {};
  maxRows: number | null = null;
  isStartingJob = false;
  
  // Job parameter info from config
  jobParameterInfo: { [key: string]: { type: string; description: string; required: boolean; default?: any } } = {};

  // Log viewing state
  selectedJobExecutionForLogs:
    | EntityModelJobExecutionDto
    | undefined = undefined;
  jobLogs: PagedModelEntityModelLogMessageDto | undefined = undefined;
  logPageSize: number = 20;
  logCurrentPage: number = 0;
  logSort: string[] = [];

  // Rollback state
  rollbackStatuses: Map<number, RollbackStatusDto> = new Map();
  rollbackReports: Map<number, RollbackReport> = new Map();
  rollbackReportVisible = false;
  activeRollbackReport: RollbackReport | null = null;
  activeRollbackExecutionId: number | null = null;

  ngOnInit() {
    this.columns = [
      {
        field: "name",
        header: "Name",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "jobInstances",
        header: "Job Instances",
        filterType: ColumnFilterType.Object,
        content: value => value,
        visible: true
      }
    ];
    this.filterFields = this.columns.map(value => value.field);
    this.controller.getJobInstances().subscribe(jobInstances => {
      this.loggingService.debug(
        "AdminDataimportJobsComponent",
        "Received job instances " + jobInstances
      );
      this.jobDtos = jobInstances?._embedded?.jobDtoList;
    });
    this.controller.getJobConfigs().subscribe(jobConfigs => {
      this.loggingService.debug(
        "AdminDataimportJobsComponent",
        "Received job configs " + jobConfigs
      );
      this.jobConfigDtos = jobConfigs?._embedded?.jobConfigDtoList;
    });
    
    // Initialize job parameter info
    this.initializeJobParameterInfo();
  }

  /**
   * Initialize parameter metadata for known jobs
   */
  private initializeJobParameterInfo(): void {
    this.jobParameterInfo = {
      'fileName': { type: 'string', description: 'Input file name', required: true },
      'maxRows': { type: 'number', description: 'Maximum rows to process (-1 for all)', required: false, default: -1 },
      'submissionId': { type: 'string', description: 'Submission ID to process', required: false },
      'publishImmediately': { type: 'boolean', description: 'Publish imported data immediately', required: false, default: false }
    };
  }

  /**
   * Check if a job runs without file input, i.e. launches via `POST /jobs/reindex/{name}`
   * rather than `POST /jobs/start/{name}`.
   *
   * Derived from the job config the backend publishes rather than from a hardcoded list of
   * job names: `JobManagerService.getAvailableJobs` declares a `fileName` parameter for the
   * file-based import jobs and an empty parameter map for the reindex/backfill jobs, which
   * operate purely on already-imported data. A local list has to be kept in step with that
   * switch by hand, and a missed entry is what made `assayRelativeQuantityBackfillJob`
   * unlaunchable from the UI in 1.0.76.
   */
  isReindexJob(jobName: string): boolean {
    return !this.findJobConfig(jobName)?.jobParameters?.[FILE_NAME_PARAMETER];
  }

  /**
   * Resolve a job config by name, preferring the current selection so the dialog keeps
   * working while the full config list is still loading.
   */
  private findJobConfig(jobName: string): EntityModelJobConfigDto | undefined {
    if (this.selectedJobConfigDto?.name === jobName) {
      return this.selectedJobConfigDto;
    }
    return this.jobConfigDtos?.find(config => config.name === jobName);
  }

  /**
   * Open the job launch dialog for the selected job configuration
   */
  openJobLaunchDialog(): void {
    if (!this.selectedJobConfigDto?.name) {
      this.messageService.add({
        severity: "warn",
        summary: "No Job Selected",
        detail: "Please select a job configuration first"
      });
      return;
    }

    const jobName = this.selectedJobConfigDto.name;
    this.jobLaunchDialogVisible = true;
    this.selectedFile = null;
    this.maxRows = -1;
    this.customParameters = {};
    
    // Load job-specific parameters from config
    if (this.selectedJobConfigDto.jobParameters) {
      this.customParameters = { ...this.selectedJobConfigDto.jobParameters };
    }
    
    // For file-based jobs, load available files
    if (!this.isReindexJob(jobName)) {
      this.loadAvailableFiles(jobName);
    }
  }

  /**
   * Load available files for the selected job
   */
  loadAvailableFiles(jobName: string): void {
    this.availableFiles = [];
    this.controller.listFilesForJobName(jobName).subscribe({
      next: (response: CollectionModelString) => {
        this.availableFiles = response?._embedded?.stringList || [];
        if (this.availableFiles.length === 0) {
          this.messageService.add({
            severity: "info",
            summary: "No Files Available",
            detail: `No input files found for job: ${jobName}`
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: "error",
          summary: "Error Loading Files",
          detail: `Failed to load files for job: ${jobName}`
        });
        this.availableFiles = [];
      }
    });
  }

  /**
   * Start the selected job with configured parameters
   */
  startJob(): void {
    if (!this.selectedJobConfigDto?.name) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "No job configuration selected"
      });
      return;
    }

    const jobName = this.selectedJobConfigDto.name;
    this.isStartingJob = true;

    if (this.isReindexJob(jobName)) {
      // Start reindex job (no file input)
      this.controller.startReindexJob(jobName).subscribe({
        next: (response: JobExecutionEntityModel) => {
          this.isStartingJob = false;
          this.jobLaunchDialogVisible = false;
          this.messageService.add({
            severity: "success",
            summary: "Job Started",
            detail: `Reindex job ${jobName} started successfully with execution ID: ${response?.executionId || 'N/A'}`
          });
          // Refresh job instances
          this.controller.getJobInstances().subscribe(jobInstances => {
            this.jobDtos = jobInstances?._embedded?.jobDtoList;
          });
        },
        error: (err) => {
          this.isStartingJob = false;
          this.messageService.add({
            severity: "error",
            summary: "Failed to Start Job",
            detail: `Error starting reindex job ${jobName}: ${err.message || 'Unknown error'}`
          });
        }
      });
    } else {
      // Start file-based job
      if (!this.selectedFile && this.availableFiles.length > 0) {
        this.isStartingJob = false;
        this.messageService.add({
          severity: "warn",
          summary: "No File Selected",
          detail: "Please select an input file"
        });
        return;
      }

      // For file-based jobs, we need to send the file and maxRows
      const jobDto: JobFileInputDto = {
        file: this.selectedFile || '',
        maxRows: this.maxRows !== null ? this.maxRows : -1
      };

      this.controller.startJob(jobName, jobDto).subscribe({
        next: (response: JobExecutionEntityModel) => {
          this.isStartingJob = false;
          this.jobLaunchDialogVisible = false;
          this.messageService.add({
            severity: "success",
            summary: "Job Started",
            detail: `Job ${jobName} started successfully with execution ID: ${response?.executionId || 'N/A'}`
          });
          // Refresh job instances
          this.controller.getJobInstances().subscribe(jobInstances => {
            this.jobDtos = jobInstances?._embedded?.jobDtoList;
          });
        },
        error: (err) => {
          this.isStartingJob = false;
          this.messageService.add({
            severity: "error",
            summary: "Failed to Start Job",
            detail: `Error starting job ${jobName}: ${err.message || 'Unknown error'}`
          });
        }
      });
    }
  }

  /**
   * Check if start job button should be enabled
   */
  isStartJobEnabled(): boolean {
    if (!this.selectedJobConfigDto?.name) {
      return false;
    }
    
    const jobName = this.selectedJobConfigDto.name;
    
    // For reindex jobs, always enabled
    if (this.isReindexJob(jobName)) {
      return !this.isStartingJob;
    }
    
    // For file-based jobs, need a file selected
    return !this.isStartingJob && (this.selectedFile !== null || this.availableFiles.length === 0);
  }

  /**
   * Get parameter type for display
   */
  getParameterType(paramName: string): string {
    return this.jobParameterInfo[paramName]?.type || 'string';
  }

  /**
   * Get parameter description for display
   */
  getParameterDescription(paramName: string): string {
    return this.jobParameterInfo[paramName]?.description || paramName;
  }

  /**
   * Get parameter default value
   */
  getParameterDefault(paramName: string): any {
    return this.jobParameterInfo[paramName]?.default;
  }

  /**
   * Check if parameter is required
   */
  isParameterRequired(paramName: string): boolean {
    return this.jobParameterInfo[paramName]?.required || false;
  }

  /**
   * Get job parameters from config for display
   */
  getJobParameters(): { key: string; value: string }[] {
    if (!this.selectedJobConfigDto?.jobParameters) {
      return [];
    }
    return Object.entries(this.selectedJobConfigDto.jobParameters).map(([key, value]) => ({ key, value }));
  }

  onRowExpand(event: any) {
    const jobInstance: JobInstanceDto = event.data;
    if (jobInstance?.jobExecutions) {
      for (const exec of jobInstance.jobExecutions) {
        const execId = exec?.executionId;
        if (execId != null && !this.rollbackStatuses.has(execId)) {
          this.loadRollbackStatus(execId);
        }
      }
    }
  }

  loadRollbackStatus(executionId: number) {
    this.importAdminController.getRollbackStatus(executionId).subscribe({
      next: status => {
        this.rollbackStatuses.set(executionId, status);
      },
      error: () => {
        // Silently ignore — user may not have ADMIN role for every view
      }
    });
  }

  isRollbackEligible(execution: any): boolean {
    return execution?.jobName === MZTAB_JOB_NAME;
  }

  wasRolledBack(executionId: number): boolean {
    return this.rollbackStatuses.get(executionId)?.wasRolledBack === true;
  }

  confirmRollback(execution: any) {
    this.confirmationService.confirm({
      header: "Confirm Rollback",
      message: `This will permanently delete all data imported by execution #${execution.executionId} (${execution.jobName}). This action cannot be undone. Proceed?`,
      icon: "pi pi-exclamation-triangle",
      acceptButtonStyleClass: "p-button-danger",
      acceptLabel: "Rollback",
      rejectLabel: "Cancel",
      accept: () => this.executeRollback(execution.executionId)
    });
  }

  executeRollback(executionId: number) {
    this.importAdminController.rollbackJobExecution(executionId).subscribe({
      next: report => {
        this.rollbackReports.set(executionId, report);
        this.rollbackStatuses.set(executionId, {
          jobExecutionId: executionId,
          wasRolledBack: true,
          rollbackSummary: report.warnings?.join("; ")
        });
        this.messageService.add({
          severity: "success",
          summary: "Rollback Complete",
          detail: `Execution #${executionId} rolled back successfully.`
        });
        this.showRollbackReport(executionId, report);
      },
      error: err => {
        const detail =
          err.status === 422
            ? "Only mzTabResultImportJob executions can be rolled back."
            : err.status === 404
            ? "Job execution not found."
            : "Rollback failed. Check server logs for details.";
        this.messageService.add({
          severity: "error",
          summary: "Rollback Failed",
          detail
        });
      }
    });
  }

  showRollbackReport(executionId: number, report?: RollbackReport) {
    this.activeRollbackExecutionId = executionId;
    this.activeRollbackReport =
      report ?? this.rollbackReports.get(executionId) ?? null;
    this.rollbackReportVisible = true;
  }

  expandAll() {
    this.expandedRows = this.jobInstanceDtos.reduce(
      (acc, p) => (acc[p.id] = true) && acc,
      {}
    );
  }

  collapseAll() {
    this.expandedRows = {};
  }

  loadJobLogs(
    jobInstanceDto: JobInstanceDto,
    jobExecutionId: number,
    jobId: number
  ) {
    this.selectedJobExecutionForLogs = jobInstanceDto?.jobExecutions?.find(
      e => e.executionId === jobExecutionId
    );
    this.logCurrentPage = 0;
    this.fetchJobLogs(jobId, this.logCurrentPage, this.logPageSize);
  }

  refreshJobLogs() {
    if (this.selectedJobExecutionForLogs?.jobId) {
      this.fetchJobLogs(
        this.selectedJobExecutionForLogs.jobId,
        this.logCurrentPage,
        this.logPageSize
      );
    }
  }

  fetchJobLogs(jobId: number, page: number, size: number) {
    this.controller.getJobLogs(jobId, page, size, this.logSort).subscribe({
      next: response => {
        this.jobLogs = response;
        const logCount = response?._embedded?.logMessageDtoList?.length || 0;
        if (logCount === 0) {
          this.messageService.add(<ToastMessageOptions>{
            severity: "info",
            summary: "No Logs Available",
            detail: "This job execution has no log messages."
          });
        }
      },
      error: () => {
        this.messageService.add(<ToastMessageOptions>{
          severity: "error",
          summary: "Error",
          detail: "Failed to load job logs"
        });
      }
    });
  }

  onLogPageChange(event: any) {
    if (this.selectedJobExecutionForLogs?.jobId) {
      this.logCurrentPage = Math.floor(event.first / event.rows);
      this.fetchJobLogs(
        this.selectedJobExecutionForLogs.jobId,
        this.logCurrentPage,
        this.logPageSize
      );
    }
  }

  getLogMessageContent(log: EntityModelLogMessageDto): any {
    if (log?._links?.self?.href) {
      const href = log._links.self.href;
      const id = href.substring(href.lastIndexOf("/") + 1);
      return { id, content: "Log message content available" };
    }
    return { id: "unknown", content: "Log message content unavailable" };
  }

  totalDeletedCount(report: RollbackReport): number {
    if (!report?.deletedByCollection) return 0;
    return Object.values(report.deletedByCollection).reduce((a, b) => a + b, 0);
  }

  totalSkippedCount(report: RollbackReport): number {
    if (!report?.skippedByCollection) return 0;
    return Object.values(report.skippedByCollection).reduce(
      (a, b) => a + b.length,
      0
    );
  }

  collectionEntries(
    map: { [key: string]: any } | null | undefined
  ): { key: string; value: any }[] {
    if (!map) return [];
    return Object.entries(map).map(([key, value]) => ({ key, value }));
  }

  /**
   * Refresh the list of job instances from the server
   */
  refreshJobInstances(): void {
    this.controller.getJobInstances().subscribe(jobInstances => {
      this.loggingService.debug(
        "AdminDataimportJobsComponent",
        "Refreshed job instances " + jobInstances
      );
      this.jobDtos = jobInstances?._embedded?.jobDtoList;
      // Reset selections after refresh
      this.selectedJobDto = null;
      this.selectedJobInstanceDto = null;
    });
  }
}
